/**
 * OP Billing API access. Sprint 1 criteria A1, A3, A8.
 *
 * EVERY call goes through the GENERATED client. There is no `httpClient.get` in
 * this file, no string literal endpoint assembled by hand, and no request or
 * response interface declared here — the paths come from the generated `paths`
 * type and the shapes come from `@nexao/contracts`. A backend contract change
 * therefore breaks this file at compile time, which is the entire point of
 * having generated the client (ENGINEERING-PRINCIPLES.md §3, §4).
 */
import { apiGet, apiPost, apiPut } from '@core/api/generated';
import type {
  OpBillDoctorOptionDto,
  OpBillTodaysAppointmentDto,
  BillingContextDto,
  ChargeSearchResultDto,
  OpBillAccountHeadDto,
  StaffDiscountOfferDto,
  ConsultationFeeResolutionDto,
  SearchPatientResponseDto,
  BillRegisterPageDto,
  BillDetailDto,
  PriceBillResponseDto,
  OpBillDashboardKpisDto,
  OpBillRevenueTrendPointDto,
  OpBillPaymentMixSliceDto,
  OpBillHourlyPointDto,
  OpBillTopServiceDto,
  OpBillDepartmentRevenueDto,
  OpBillDoctorRevenueDto,
  AdvancedSearchResponseDto,
  PatientClinicalTimelineResponseDto,
  OpBillCreditLedgerDto,
  OpBillDraftDto,
  OpBillPendingBookingRequest,
} from '@nexao/contracts';
import type { paths } from '@core/api/generated';

/**
 * The WIRE shape of the patient-search criteria, taken from the generated
 * client rather than from `z.infer<typeof searchPatientRequestSchema>`.
 *
 * Those two are genuinely different types and the difference bites: the Zod
 * infer describes the shape AFTER the server parses it (`dateOfBirth: Date`),
 * while JSON on the wire carries `dateOfBirth: string | null`. A browser sends
 * the wire shape, so the generated type is the correct authority here — and
 * this mismatch is the sort of thing a hand-written client hides by casting.
 */
type PatientSearchCriteria = NonNullable<
  NonNullable<
    paths['/api/v1/patients/search']['post']['requestBody']
  >['content']['application/json']['criteria']
>;

/**
 * The workstation's FIRST call — one request for cashier, counter, cash session,
 * financial year, accounting period, payment modes, permissions and policies
 * (Billing Context Rule 1). Twenty calls to open a screen is the failure this
 * shape exists to prevent.
 */
/**
 * The cash counter this caller's ROLE assigns them to, if any.
 *
 * NO PARAMETERS — the answer is a property of who is calling, not of anything
 * they can ask for. A `?userId=` would let one cashier read another's desk
 * assignment; the server takes the caller from the request context.
 *
 * Consumed by the SIGN-IN GATE, which runs before a branch or a counter is
 * known — which is why it is separate from the billing context below rather
 * than a field on it.
 */
export async function getAssignedCounter(signal?: AbortSignal): Promise<
  paths['/api/v1/op-billing/assigned-counter']['get']['responses'][200]['content']['application/json']['data']
> {
  return apiGet('/api/v1/op-billing/assigned-counter', undefined, { signal });
}

export async function getBillingContext(
  params: { branchId?: string; counterId?: string },
  signal?: AbortSignal,
): Promise<BillingContextDto> {
  return apiGet('/api/v1/op-billing/billing-context', params, { signal });
}

/**
 * Charge discoverability. Returns NO price, and must never be made to — a search
 * result has no payer, package or date behind it, so any figure it carried would
 * be a different number from the one billed (Search Rule).
 */
export async function searchCharges(
  params: {
    q?: string;
    departmentId?: string;
    categoryId?: string;
    accountHeadId?: string;
    hasIdentifier?: boolean;
    identifierPrefix?: string;
    barcode?: string;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<ChargeSearchResultDto[]> {
  // Asserted to the CONTRACT type, as `getBill` and `priceDraft` are and for the
  // same reason: `standardPricePaise` (the C1 rate) was added to
  // `chargeSearchResultResponseSchema` and `docs/openapi.json` has not been
  // regenerated since, so the generated client still describes the older shape.
  // The contract is the authority; the assertion goes away on the next
  // `npm run api:generate`.
  return apiGet('/api/v1/op-billing/charges/search', params, { signal }) as Promise<ChargeSearchResultDto[]>;
}

/**
 * The account heads the catalog's Account Head tab lists, each with how many
 * billable services sit under it.
 *
 * NOT Administration's `/account-heads` — that one takes CHARGE_MASTER_READ,
 * which a cashier has no reason to hold, so calling it from here would 403 the
 * people who need it. Same table, billing permission.
 */
export async function listBillingAccountHeads(signal?: AbortSignal): Promise<OpBillAccountHeadDto[]> {
  // No params at all — the generated client types this operation's query as
  // `undefined`, because the route takes none.
  return apiGet('/api/v1/op-billing/account-heads', undefined, { signal });
}

/**
 * Replace a draft's lines. CAS-guarded by `expectedLockVersion` (ADR-063): a
 * mismatch is a 409, never a silent merge.
 *
 * WHY THIS EXISTS NOW. The workstation created a draft once and never updated
 * it, so anything added or changed after the first sync — a second service, a
 * quantity, and now the RATE on an open account head line — never reached the
 * server. `useDraftLines`'s own header always said Sprint 3 would sync through
 * this endpoint; the endpoint has been live since Phase 4C.1.
 */
export async function updateDraft(
  draftId: string,
  body: {
    lines: Array<{
      chargeId?: string;
      accountHeadId?: string;
      unitRatePaise?: number;
      description?: string;
      quantity?: number;
      remarks?: string;
      doctorId?: string;
      lineRef?: string;
    }>;
    /**
     * The category (C1-C4) this whole bill is priced at (ADR-082).
     *
     * Bill-level by design — the desk's rule is that one category governs
     * every line ("no changes of individual line item"). Absent means C1, the
     * unconditional STANDARD an OP bill always resolved to.
     */
    chargeTier?: 'C1' | 'C2' | 'C3' | 'C4';
    /**
     * The doctor answer, sent with every sync (desk, 2026-10-13): a `doctorId`, or `noDoctor: true` for "Others" — never both,
     * or neither when the screen has no opinion yet (the draft's doctor is then left as it is).
     */
    doctorId?: string;
    noDoctor?: boolean;
    /** The baby on a newborn's bill: values, or `null`s to clear; absent leaves the draft's as it is. */
    newbornName?: string | null;
    newbornAgeDays?: number | null;
    newbornGender?: string | null;

    expectedLockVersion: number;
  },
  signal?: AbortSignal,
): Promise<unknown> {
  return apiPut('/api/v1/op-billing/drafts/{draftId}', body as never, { pathParams: { draftId }, signal });
}

/**
 * Doctors billable against a department — the per-line picker's options.
 *
 * Returns USER ids: `FinInvoiceLine.doctorId` is a foreign key to `users`, not
 * to `doctors`, and a `Doctor.id` here would match zero rows and fail silently
 * as a blank column on every revenue report.
 */
export async function listBillingDoctors(
  departmentId?: string,
  signal?: AbortSignal,
): Promise<OpBillDoctorOptionDto[]> {
  return apiGet('/api/v1/op-billing/doctors', departmentId ? { departmentId } : {}, { signal });
}

/**
 * Today's appointments for the patient at the counter.
 *
 * The counter recommends its doctor from this rather than making the cashier
 * pick out of every doctor in the hospital — the appointment already knows who
 * the patient is seeing, and a second answer at the counter is how a bill gets
 * attributed to someone they never saw.
 */
export async function listTodaysAppointments(
  patientId: string,
  signal?: AbortSignal,
): Promise<OpBillTodaysAppointmentDto[]> {
  return apiGet('/api/v1/op-billing/patients/{patientId}/today-appointments', undefined, {
    pathParams: { patientId },
    signal,
  });
}

/**
 * Patient lookup for the billing header.
 *
 * `POST`, not `GET`, because that is what the server serves — the criteria are a
 * structured object, not a flat query string.
 *
 * The criteria field names are READ FROM THE CONTRACT, not guessed. The first
 * draft of this function invented `{ query, mobile }` and cast the body to
 * `never` to silence the compiler; the real contract has neither field, and the
 * cast would have turned a compile-time error into an empty result list in
 * production. `ENGINEERING-PRINCIPLES.md` §1 names that reflex exactly — the
 * assertion came from what was expected rather than from what was frozen.
 */
export async function searchPatients(
  criteria: PatientSearchCriteria,
  signal?: AbortSignal,
): Promise<SearchPatientResponseDto> {
  return apiPost(
    '/api/v1/patients/search',
    { criteria, pagination: { page: 1, limit: 20 } },
    { signal },
  ) as Promise<SearchPatientResponseDto>;
}

/**
 * The bill register, filtered to one patient — the source for the summary rail's
 * billing history.
 *
 * Its response type is published in `@nexao/contracts` and documented in
 * OpenAPI, so this function declares no shape of its own (criterion A8).
 */
/**
 * The bill register with its full filter set.
 *
 * CURSOR, NOT OFFSET. The underlying table takes concurrent inserts all day, and
 * offset paging under concurrent inserts both duplicates and skips rows mid-page.
 * The cursor is opaque here on purpose — the server decides where the next page
 * starts, and a client computing an offset would be re-deriving pagination it
 * was handed.
 */
export async function listBills(
  filters: {
    fromDate?: string;
    toDate?: string;
    patientId?: string;
    doctorId?: string;
    departmentId?: string;
    status?: string;
    limit?: number;
    cursor?: string;
    /** The Credit Register tab — see `listOpBillsQuerySchema.onCredit`. */
    onCredit?: boolean;
    /** The Private Patient Register tab — see `listOpBillsQuerySchema.isWalkIn`. */
    isWalkIn?: boolean;
  },
  signal?: AbortSignal,
): Promise<BillRegisterPageDto> {
  // Asserted to the CONTRACT type, as the other register calls are: the row
  // gained `cashierName` and `createdAt` and `docs/openapi.json` has not been
  // regenerated since, so the generated client still describes the older shape.
  return apiGet('/api/v1/op-billing/bills', filters as never, { signal }) as Promise<BillRegisterPageDto>;
}

export async function listPatientBills(
  patientId: string,
  limit: number,
  signal?: AbortSignal,
): Promise<BillRegisterPageDto> {
  return apiGet('/api/v1/op-billing/bills', { patientId, limit }, { signal }) as Promise<BillRegisterPageDto>;
}

/**
 * One posted bill with its lines — what the register's View action opens.
 *
 * Gated on `receivable:ledger:read`, the same authority as the register itself
 * rather than a billing permission: reading what was billed is an accounts
 * question, and an auditor needs it without ever being able to raise a bill.
 */
export async function getBill(
  invoiceId: string,
  signal?: AbortSignal,
  /**
   * THE PATIENT THIS IS BEING ENQUIRED ABOUT, when opened from the Enquire tab.
   *
   * Sent so the server can apply the enquiry rule rather than the cashier fence
   * (desk, 2026-09-09: "enquire tab should show all info of the op number
   * instead of user created"). It is VERIFIED against the bill server-side, so
   * it widens a read to this patient's own documents and to nothing else —
   * passing a mismatched id falls back to the ordinary fence.
   */
  patientId?: string,
): Promise<BillDetailDto> {
  // Asserted to the CONTRACT type, exactly as `priceDraft` does above and for
  // the same reason: this endpoint's OpenAPI entry names a `BillDetailResponse`
  // schema that was never defined, so the generated client types it `unknown`.
  // `billDetailResponseSchema` (added alongside this caller) is now the single
  // declaration of the shape; the assertion goes away once the operation is
  // regenerated against it.
  return apiGet('/api/v1/op-billing/bills/{invoiceId}', (patientId ? { patientId } : {}) as never, {
    pathParams: { invoiceId },
    signal,
  }) as Promise<BillDetailDto>;
}

// ---------------------------------------------------------------------------
// Dashboard — SEVEN endpoints, one per card (Dashboard Rule 2).
//
// Not one payload. Each card gets independent caching, its own permission, its
// own SLA and its own observability, and a widget can change without a contract
// revision for the other six. A single mega-endpoint would couple all seven and
// make the slowest one everybody's problem.
// ---------------------------------------------------------------------------

export async function getDashboardKpis(date?: string, signal?: AbortSignal): Promise<OpBillDashboardKpisDto> {
  return apiGet('/api/v1/op-billing/dashboard/kpis', { date }, { signal });
}

export async function getRevenueTrend(days?: number, signal?: AbortSignal): Promise<OpBillRevenueTrendPointDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/revenue-trend', { days }, { signal });
}

export async function getPaymentMix(date?: string, signal?: AbortSignal): Promise<OpBillPaymentMixSliceDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/payment-mix', { date }, { signal });
}

export async function getHourlyBilling(date?: string, signal?: AbortSignal): Promise<OpBillHourlyPointDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/hourly', { date }, { signal });
}

export async function getTopServices(limit?: number, date?: string, signal?: AbortSignal): Promise<OpBillTopServiceDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/top-services', { limit, date }, { signal });
}

export async function getDepartmentRevenue(date?: string, signal?: AbortSignal): Promise<OpBillDepartmentRevenueDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/department-revenue', { date }, { signal });
}

export async function getDoctorRevenue(date?: string, signal?: AbortSignal): Promise<OpBillDoctorRevenueDto[]> {
  return apiGet('/api/v1/op-billing/dashboard/doctor-revenue', { date }, { signal });
}

/**
 * Multi-mode patient lookup — UHID, mobile, ABHA, government ID, policy number,
 * legacy MR number.
 *
 * `POST /patients/advanced-search` rather than `/patients/search`, because the
 * latter's criteria are purely demographic and cannot reach a phone number. At a
 * billing counter, "find them by the mobile they just recited" is the single
 * commonest lookup.
 */
export async function advancedSearchPatients(
  criteria: Record<string, string>,
  signal?: AbortSignal,
): Promise<AdvancedSearchResponseDto> {
  return apiPost('/api/v1/patients/advanced-search', criteria as never, { signal }) as Promise<AdvancedSearchResponseDto>;
}

/**
 * The patient's clinical timeline — the Audit Timeline in the summary rail.
 *
 * This is the one "history" surface the contract actually exposes for a patient.
 * Visit, encounter and alert reads do not exist, which is why the rail discloses
 * those rather than showing an empty panel that reads as "none".
 */
export async function getPatientTimeline(
  patientId: string,
  signal?: AbortSignal,
): Promise<PatientClinicalTimelineResponseDto> {
  return apiGet('/api/v1/clinical/patients/{patientId}/timeline', undefined, {
    pathParams: { patientId },
    signal,
  }) as Promise<PatientClinicalTimelineResponseDto>;
}

// ---------------------------------------------------------------------------
// COMMANDS — the write path.
//
// Every one goes through `OpBillApplicationService` via its own endpoint. There
// is no "create and post" shortcut and there must not be: one request maps to
// exactly one application-service command (§6A.4), because a combined call
// cannot be retried safely — a timeout would leave the caller unable to tell
// which half happened.
// ---------------------------------------------------------------------------

/** Open a draft. Carries billing INTENT only — no amount is an input (§6A.10). */
export async function createDraft(
  body: {
    branchId: string;
    patientId?: string;
    /**
     * A WALK-IN bill: someone who is not a patient. Required when there is no
     * `patientId` — the server refuses a bill that names nobody, and the
     * database carries the same rule as a CHECK constraint.
     */
    walkInName?: string;
    walkInMobile?: string;
    /** Optional, same posture as `walkInMobile`. See `FinInvoice.walkInGender`. */
    walkInGender?: string;
    /** Optional, same posture as `walkInMobile`. See `FinInvoice.walkInAgeYears`. */
    walkInAgeYears?: number;
    /** A newborn's test billed to the mother — all three or none. See `DraftInput.newborn`. */
    newbornName?: string;
    newbornAgeDays?: number;
    newbornGender?: string;
    doctorId?: string;
    /** "Others" — no doctor involved (desk, 2026-10-13). Sent instead of `doctorId`, never with it; the draft remembers it. */
    noDoctor?: boolean;
    departmentId?: string;
    /**
     * The category (C1-C4) this whole bill is priced at (ADR-082).
     *
     * Bill-level by design — the desk's rule is that one category governs
     * every line ("no changes of individual line item"). Absent means C1, the
     * unconditional STANDARD an OP bill always resolved to.
     */
    chargeTier?: 'C1' | 'C2' | 'C3' | 'C4';

    counterId?: string;
    /**
     * THE APPOINTMENT THIS CONSULTATION BILL IS FOR — an existing one (a
     * future booking, already `PENDING_CONFIRMATION`).
     */
    appointmentId?: string;
    /**
     * A slot was picked and no appointment exists yet (desk, 2026-09-11).
     * Mutually exclusive with `appointmentId` above — the server rejects both
     * together. See `opBillPendingBookingSchema` for the full shape.
     */
    pendingBooking?: OpBillPendingBookingRequest;
    /**
     * EXACTLY ONE of chargeId / accountHeadId per line — the server's schema
     * refines it, and this shape mirrors that rather than restating it loosely.
     * `unitRatePaise` belongs to a head line only: a catalogue line carrying a
     * rate is rejected, because "which price won?" must never be ambiguous.
     */
    lines: Array<{
      chargeId?: string;
      accountHeadId?: string;
      unitRatePaise?: number;
      description?: string;
      quantity?: number;
      remarks?: string;
      doctorId?: string;
      lineRef?: string;
    }>;
  },
  signal?: AbortSignal,
): Promise<unknown> {
  return apiPost('/api/v1/op-billing/drafts', body as never, { signal });
}

/**
 * Post the draft: allocate the statutory invoice number, price, tax, post to GL
 * and AR, and optionally collect.
 *
 * DRAFT IDENTITY IS THE IDEMPOTENCY KEY (§C5). A retry after a timeout re-posts
 * the same draft and receives the SAME invoice back rather than raising a second
 * one — which is why this takes a draftId rather than a payload.
 */
export async function postBill(
  draftId: string,
  body: {
    payments?: Array<{ paymentModeId: string; amountPaise: number; referenceNumber?: string }>;
    /**
     * Employee / Company Credit. Mutually exclusive with `payments` — the
     * server rejects both together rather than guessing which the operator
     * meant, because the same rupees would be recorded as collected AND owed.
     */
    creditParty?: { partyType: 'EMPLOYEE' | 'COMPANY'; employeeId?: string; payerId?: string };
    acknowledgedWarnings?: string[];
    /**
     * Ask the server to refuse a post that repeats a bill raised in the last 30
     * minutes (409 `FIN_OP_BILL_REPEAT_SUSPECTED`). Send `reason` on the second
     * attempt to post it anyway (the poster needs the repeat-override permission); the
     * reason is audited. THE SERVER ASKS EITHER WAY - leaving the object out does not
     * skip the check, it only means no reason was given.
     */
    repeatBillCheck?: { reason?: string };
  },
  signal?: AbortSignal,
  /**
   * `idempotencyKey` is sent as the `Idempotency-Key` header. The same key with the same
   * payment/credit body means "this is the SAME attempt": if the first one committed and
   * only its answer was lost, the server hands back that original result (200, replay)
   * instead of posting again; the same key with a DIFFERENT body is refused (409).
   * See `postIdempotencyKey`.
   */
  options?: { idempotencyKey?: string },
): Promise<unknown> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/post', body as never, {
    pathParams: { draftId },
    signal,
    ...(options?.idempotencyKey ? { headers: { 'Idempotency-Key': options.idempotencyKey } } : {}),
  });
}

/**
 * Employee / Company Credit ledgers.
 *
 * Every figure is the SERVER's, read from the invoice rather than a stored
 * copy, so a credit bill that is later collected or cancelled leaves the
 * ledger on its own.
 */
export async function getEmployeeCreditLedger(employeeId: string, signal?: AbortSignal): Promise<OpBillCreditLedgerDto> {
  return apiGet('/api/v1/op-billing/credit-ledger/employees/{employeeId}', undefined, {
    pathParams: { employeeId },
    signal,
  }) as Promise<OpBillCreditLedgerDto>;
}

export async function getCompanyCreditLedger(payerId: string, signal?: AbortSignal): Promise<OpBillCreditLedgerDto> {
  return apiGet('/api/v1/op-billing/credit-ledger/companies/{payerId}', undefined, {
    pathParams: { payerId },
    signal,
  }) as Promise<OpBillCreditLedgerDto>;
}

/** Park a draft with a reason. Held drafts block their counter's cash session from closing (§C7). */
export async function holdDraft(draftId: string, holdReason: string, expectedLockVersion: number, signal?: AbortSignal): Promise<unknown> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/hold', { holdReason, expectedLockVersion } as never, {
    pathParams: { draftId },
    signal,
  });
}

// ---------------------------------------------------------------------------
// Drafts as a LIST — saved and held bills, and getting back into one.
//
// The endpoints existed from Phase 4B and nothing in the browser ever called
// three of them: a bill could be saved or held and then never found again. The
// Hold Bills tab is what reads them.
//
// SHAPES COME FROM `@nexao/contracts` (`opBillDraftResponseSchema`), not from
// here. This operation's OpenAPI entry names no response schema, so the
// generated client types it `unknown` and the call is asserted — the same gap
// `getBill` documents above. Restating the shape in this file instead is what
// acceptance criterion A8 fails the build on.
// ---------------------------------------------------------------------------

/**
 * The caller's own open drafts — both SAVED (`DRAFT`) and PARKED (`HELD`).
 *
 * Deliberately sends no `counterId`: with one the server switches to "everything
 * HELD at that counter", which is the cash-session question (§C7), not "what was
 * I working on". Saved drafts would vanish from the list the moment it was
 * passed, and Save draft is half of what this list exists to show.
 */
export async function listMyDrafts(signal?: AbortSignal): Promise<OpBillDraftDto[]> {
  return apiGet('/api/v1/op-billing/drafts', {} as never, { signal }) as Promise<OpBillDraftDto[]>;
}

/** One draft, with its lines — what Resume loads back into the counter. */
export async function getDraft(draftId: string, signal?: AbortSignal): Promise<OpBillDraftDto> {
  return apiGet('/api/v1/op-billing/drafts/{draftId}', {} as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<OpBillDraftDto>;
}

/**
 * Take a held bill back into work: `HELD` → `DRAFT`, hold reason cleared.
 *
 * A `DRAFT` row is resumable too, and the server accepts it — a saved draft is
 * already in work, so this is a no-op for it rather than an error, and the
 * counter does not have to branch on status before opening one.
 */
export async function resumeDraft(draftId: string, expectedLockVersion: number, signal?: AbortSignal): Promise<OpBillDraftDto> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/resume', { expectedLockVersion } as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<OpBillDraftDto>;
}

/** Abandon it. `DISCARDED` is terminal — the row stays for the audit trail. */
export async function discardDraft(
  draftId: string,
  expectedLockVersion: number,
  signal?: AbortSignal,
  /** Close a waiting or approved discount request with the draft. Without it the server refuses (409 `FIN_OP_DRAFT_HAS_APPROVALS`). */
  withdrawApprovals?: boolean,
): Promise<OpBillDraftDto> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/discard', { expectedLockVersion, ...(withdrawApprovals ? { withdrawApprovals } : {}) } as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<OpBillDraftDto>;
}

/**
 * Collect against a POSTED invoice.
 *
 * Split across up to ten modes. The server decides what is owed and rejects a
 * total exceeding the bill — the client supplies only what the patient handed
 * over and through which mode.
 */
export async function collectPayment(
  invoiceId: string,
  body: {
    payments: Array<{ paymentModeId: string; amountPaise: number; referenceNumber?: string }>;
    /**
     * THE DESK. The server resolves the cashier's open session FROM this and
     * stamps every payment line with it. Omitted, the collection is attributed
     * to no session and `fin_payment_lines.cash_session_id` lands NULL — which
     * is how all 15 rows in production came to have one.
     */
    counterId?: string;
    /** Verified against the resolved session, never trusted. */
    cashSessionId?: string;
  },
  signal?: AbortSignal,
): Promise<unknown> {
  return apiPost('/api/v1/op-billing/bills/{invoiceId}/collect-payment', body as never, {
    pathParams: { invoiceId },
    signal,
  });
}

/**
 * Price the draft as it stands — per-line rate, discount, tax and net, plus the
 * bill total.
 *
 * READ-ONLY: no number drawn, no row written. Safe to call on every grid edit.
 *
 * The figures are "what it costs right now", NOT a quote the server is bound to
 * — `postBill` re-resolves everything, because a tariff can activate or a
 * package be consumed between the preview and the post.
 */
export async function priceDraft(draftId: string, signal?: AbortSignal): Promise<PriceBillResponseDto> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/price', undefined as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<PriceBillResponseDto>;
}

/**
 * Price lines WITHOUT a saved draft - what the counter asks while the operator
 * is still building the bill.
 *
 * `POST /op-billing/price-preview`: the same engine and the same answer as
 * `priceDraft`, with nothing stored. It is why a bill that is cancelled leaves no
 * draft behind: a draft is only made when something needs one.
 */
export async function priceBillPreview(
  body: {
    branchId: string;
    patientId?: string;
    walkInName?: string;
    walkInMobile?: string;
    walkInGender?: string;
    walkInAgeYears?: number;
    doctorId?: string;
    departmentId?: string;
    payerId?: string;
    counterId?: string;
    chargeTier?: 'C1' | 'C2' | 'C3' | 'C4';
    lines: Array<{
      chargeId?: string;
      accountHeadId?: string;
      unitRatePaise?: number;
      description?: string;
      quantity?: number;
      remarks?: string;
      doctorId?: string;
      chargeTier?: 'C1' | 'C2' | 'C3' | 'C4';
      lineRef?: string;
    }>;
  },
  signal?: AbortSignal,
): Promise<PriceBillResponseDto> {
  return apiPost('/api/v1/op-billing/price-preview', body as never, { signal }) as Promise<PriceBillResponseDto>;
}

/**
 * Print a posted bill.
 *
 * The SERVER builds the document from each line's own snapshot and records the
 * print — `printCount` and `isDuplicate` come back with it, so a reprint can be
 * watermarked and numbered rather than looking identical to the original.
 *
 * ORIGINAL vs DUPLICATE is the server's decision, not the caller's: it knows
 * whether this invoice has been printed before, and letting the browser assert
 * "this is an original" is how two originals of one bill reach a patient.
 *
 * WHICH IS WHY `'ORIGINAL'` WAS NEVER A VALID KIND. This defaulted to it, but
 * `opBillPrintKindSchema` accepts only BILL | RECEIPT | DUPLICATE | ESTIMATE —
 * so every print 422'd, including New Bill's own Print Bill button. The comment
 * above was right about the intent and the code contradicted it: the caller asks
 * for a BILL and the server decides whether that copy is a duplicate.
 */
export type OpBillPrintKind = 'BILL' | 'RECEIPT' | 'DUPLICATE' | 'ESTIMATE';

export async function printBill(invoiceId: string, kind: OpBillPrintKind = 'BILL', reason?: string): Promise<unknown> {
  return apiPost('/api/v1/op-billing/bills/{invoiceId}/print', { kind, reason } as never, {
    pathParams: { invoiceId },
  });
}


/**
 * Set the draft's BILL-LEVEL discount — a percent of the gross, or a flat
 * amount in paise. Exactly one.
 *
 * The response says whether it APPLIED or needs approval; the figures come from
 * re-pricing the draft, never from here. `financial:discount:apply`.
 */
export async function setBillDiscount(
  draftId: string,
  body: { percent?: number; amountPaise?: number; reason?: string },
  signal?: AbortSignal,
): Promise<{
  status: string;
  requiresApproval: boolean;
  requestedPercent: number | null;
  requestedAmountPaise: number | null;
  maxPercentWithoutApproval: number | null;
}> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/discount', body as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<{
    status: string;
    requiresApproval: boolean;
    requestedPercent: number | null;
    requestedAmountPaise: number | null;
    maxPercentWithoutApproval: number | null;
  }>;
}

/** Take the discount off the draft. */
export async function clearBillDiscount(draftId: string, signal?: AbortSignal): Promise<{ cleared: number }> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/discount/clear', {} as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<{ cleared: number }>;
}


/* ------------------------------------------------------------------ *
 * DECIDING A DISCOUNT — the approver's side (desk, 2026-08-31).
 *
 * "when frontdesk is applied discount admin should approve this discount…
 *  instead of generate bill just give approve button. so admin give generate
 *  bill."
 *
 * All three carry `financial:op-bill:discount-approve`, including the READ:
 * a pending discount names a patient, their bill total and the cashier who
 * asked to reduce it, so nobody browses this queue who cannot act on it.
 * ------------------------------------------------------------------ */

export interface PendingDiscountApproval {
  approvalId: string;
  /** CAS token. Send it back untouched — it is what stops two approvers colliding. */
  lockVersion: number;
  draftId: string | null;
  invoiceId: string | null;
  requestedPercent: number | null;
  requestedAmountPaise: number | null;
  reason: string | null;
  requestedAt: string;
  requestedByName: string | null;
  requestedByEmployeeCode: string | null;
  patientName: string | null;
  patientUhid: string | null;
  draftStatus: string | null;
  estimatedNetPaise: number | null;
}

export async function listDiscountApprovals(signal?: AbortSignal): Promise<PendingDiscountApproval[]> {
  return apiGet('/api/v1/op-billing/discount-approvals', {} as never, { signal }) as Promise<PendingDiscountApproval[]>;
}

/**
 * Approve. Omitting `percent`/`amountPaise` sanctions exactly what was asked
 * for — the common case, and the server refuses anything ABOVE the request.
 */
export async function approveDiscount(
  approvalId: string,
  body: { lockVersion: number; percent?: number; amountPaise?: number; reason?: string },
  signal?: AbortSignal,
): Promise<{ approvalId: string; status: string; approvedPercent: number | null; approvedAmountPaise: number | null }> {
  return apiPost('/api/v1/op-billing/discount-approvals/{approvalId}/approve', body as never, {
    pathParams: { approvalId },
    signal,
  }) as Promise<{ approvalId: string; status: string; approvedPercent: number | null; approvedAmountPaise: number | null }>;
}

/** Refuse. The reason is mandatory — the counter has to tell the patient something. */
export async function rejectDiscount(
  approvalId: string,
  body: { lockVersion: number; reason: string },
  signal?: AbortSignal,
): Promise<{ approvalId: string; status: string }> {
  return apiPost('/api/v1/op-billing/discount-approvals/{approvalId}/reject', body as never, {
    pathParams: { approvalId },
    signal,
  }) as Promise<{ approvalId: string; status: string }>;
}


/* ------------------------------------------------------------------ *
 * STAFF DISCOUNT — an admin-set concession, given by naming an employee.
 *
 * NOTE WHAT NONE OF THESE SEND: an amount. The rule the administrator set
 * decides what comes off, so the counter has no figure to transmit and none to
 * edit. That is what "non-editable discount value" means here — not a disabled
 * input, but no input at all.
 * ------------------------------------------------------------------ */

/**
 * WHAT A DOCTOR'S CONSULTATION COSTS — head, amount and split, already decided.
 *
 * The appointment desks display this. They no longer ask the clerk which
 * account head to bill or how much: the hospital states both on the doctor,
 * and this is that statement. `doctorUserId` is a USER id — the id every
 * appointment screen holds, since `Appointment.doctorId` is a foreign key to
 * User rather than to the Doctor master.
 *
 * Rejects with the server's own message when the doctor has no fee
 * configured, and that message names Doctor Master, because the clerk hitting
 * it has a patient at the counter and needs to know who can fix it.
 */
export async function getConsultationFee(
  doctorUserId: string,
  signal?: AbortSignal,
): Promise<ConsultationFeeResolutionDto> {
  return apiGet('/api/v1/op-billing/consultation-fee/{doctorUserId}', undefined, {
    pathParams: { doctorUserId },
    signal,
  }) as Promise<ConsultationFeeResolutionDto>;
}

/** The staff rule in force for an account head. `offer` is null when there is none. */
export async function getStaffDiscountOffer(
  accountHeadId: string,
  signal?: AbortSignal,
): Promise<{ offer: StaffDiscountOfferDto | null }> {
  return apiGet('/api/v1/op-billing/staff-discount/account-heads/{accountHeadId}', undefined, {
    pathParams: { accountHeadId },
    signal,
  }) as Promise<{ offer: StaffDiscountOfferDto | null }>;
}

/** Give it, to this employee — or, for a Student, to this name/department/section. Applied before pricing so the total shown includes it. */
/** Who the concession is for — the counter's dropdown. */
export type StaffBeneficiaryCategory = 'STAFF' | 'STUDENT' | 'DEPENDENT' | 'RETIRED' | 'CAMP';

/**
 * A Student's — or, since 2026-09-23, a Retired staff member's, or, since
 * 2026-10-01, a Camp's — free-text identity (a camp's is its name). Named for
 * Student, which needed it first (desk, 2026-09-17:
 * "for student need to ask enter name, department, section"); there is no
 * student directory anywhere in this system (only the `Employee` master a
 * Student is not), so this is captured rather than looked up. Retired staff
 * DO have an `Employee` row, but it is routinely marked inactive — the desk
 * asked to type the name rather than fight a picker over it.
 *
 * `department`/`section` are OPTIONAL — desk decision, 2026-09-22: the name
 * alone is enough to give the discount now.
 */
export interface ConcessionStudent {
  name: string;
  department?: string;
  section?: string;
}

/**
 * Give the staff concession to a named employee, or — for a Student or
 * Retired staff — to a name/department/section typed in instead. Exactly one
 * of the two; the server refuses both or neither.
 *
 * THE AMOUNT IS NEVER SENT, and that is the design rather than an omission:
 * the browser transmits WHO the concession is for and a category, and the
 * SERVER decides the figure from the rule. There is no field to tamper with
 * and no way for the screen and the invoice to disagree about what came off.
 *
 * `category` decides WHICH rule applies — a staff member gets 30% on a lab
 * line, a student gets nothing there and ₹100 on an OP one.
 *
 * `doctorUserId` is CONSULTATION-FEE-ONLY. Naming the doctor this
 * concession's line belongs to lets the server recognise the one doctor
 * (C.M.O., Casualty) whose consultation is waived in full — see
 * `StaffDiscountService.findConsultationOffer`. Universal Billing never
 * passes it, and its own concessions resolve exactly as they always have.
 */
export async function applyStaffDiscount(
  draftId: string,
  beneficiary: { employeeId: string } | { student: ConcessionStudent },
  category?: StaffBeneficiaryCategory,
  options?: { doctorUserId?: string; relation?: 'SPOUSE' | 'SON' | 'DAUGHTER'; signal?: AbortSignal },
): Promise<{ linesDiscounted: number }> {
  return apiPost(
    '/api/v1/op-billing/drafts/{draftId}/staff-discount',
    { ...beneficiary, category, doctorUserId: options?.doctorUserId, relation: options?.relation } as never,
    { pathParams: { draftId }, signal: options?.signal },
  ) as Promise<{ linesDiscounted: number }>;
}

/**
 * What concession a DOCTOR'S OWN consultation would carry, for a category —
 * the Concession tab's preview, before anything is applied. See
 * `applyStaffDiscount`'s note on `doctorUserId`: Universal Billing has no
 * equivalent call, because it bills no single doctor's line.
 */
export async function getConsultationConcessionOffer(
  doctorUserId: string,
  category: StaffBeneficiaryCategory,
  signal?: AbortSignal,
): Promise<{ offer: StaffDiscountOfferDto | null }> {
  return apiGet(
    '/api/v1/op-billing/consultation-fee/{doctorUserId}/concession-offer',
    { category },
    { pathParams: { doctorUserId }, signal },
  ) as Promise<{ offer: StaffDiscountOfferDto | null }>;
}

/** Withdraw it — the box was unticked, or the employee changed. */
export async function clearStaffDiscount(draftId: string, signal?: AbortSignal): Promise<{ cleared: number }> {
  return apiPost('/api/v1/op-billing/drafts/{draftId}/staff-discount/clear', {} as never, {
    pathParams: { draftId },
    signal,
  }) as Promise<{ cleared: number }>;
}
